import { CKBox, CKButton, CKLayoutGridAuto } from 'clearkit';
import { useState } from 'react';

import { AuthLayoutHeader } from '~/components/layouts/AuthLayout';

import TextFieldWithValidations from '../../shared/TextFieldWithValidations';
import useCreateAccountAndSignIn from '../../shared/useCreateAccountAndSignIn';
import useTelemetryId from '../../shared/useTelemetryId';
import { CreateAccountInput } from '../types';

type CreateAccountProps = {
  proposedOrg: CreateAccountInput;
};

type CreateAccountValidationErrors = {
  account_name?: string[];
  user_first_name?: string[];
  user_last_name?: string[];
};

export default function CreateAccount({ proposedOrg }: CreateAccountProps) {
  const [userFirstName, setUserFirstName] = useState(
    proposedOrg.user_first_name || '',
  );
  const [userLastName, setUserLastName] = useState(
    proposedOrg.user_last_name || '',
  );
  const [accountName, setAccountName] = useState(
    proposedOrg.organization_name || '',
  );
  const [validationErrors, setValidationErrors] = useState<
    CreateAccountValidationErrors
  >({});

  const { isLoading, mutateAsync } = useCreateAccountAndSignIn();
  const { getTelemetryId } = useTelemetryId();

  async function createAccount() {
    const telemetryId = await getTelemetryId();

    const { isSuccess, error, redirect } = await mutateAsync({
      accountName,
      userFirstName,
      userLastName,
      telemetryId,
    });

    if (isSuccess) {
      setValidationErrors({});
      window.location.href = redirect;
      return;
    }

    if (
      error.action === 'unauthorized' ||
      error?.error.message_code === 'login_error'
    ) {
      window.location.href = `/auth/login?error=${error.type ||
        error?.error.message_code}`;
      return;
    }

    if (error.error?.type === 'validation') {
      setValidationErrors(error.error.errors);
      return;
    }
  }

  return (
    <>
      <AuthLayoutHeader
        className="sm:w-80"
        description="Provide your details below and we'll create your account."
        header={
          <div className="w-full font-semibold">
            Create your Clearbit account
          </div>
        }
      />
      <CKBox className="w-full p-5 sm:w-[440px]">
        <div className="flex flex-col gap-6">
          <CKLayoutGridAuto gap="1.5rem">
            <TextFieldWithValidations
              name="user_first_name"
              onChange={setUserFirstName}
              placeholder="First name"
              validationErrors={validationErrors}
              value={userFirstName}
            />
            <TextFieldWithValidations
              name="user_last_name"
              onChange={setUserLastName}
              placeholder="Last name"
              validationErrors={validationErrors}
              value={userLastName}
            />
          </CKLayoutGridAuto>
          <TextFieldWithValidations
            name="account_name"
            onChange={setAccountName}
            placeholder="Company name"
            validationErrors={validationErrors}
            value={accountName}
          />
          <div>
            <CKButton
              isLoading={isLoading}
              onClick={createAccount}
              variant="bold"
              variantColor="blue"
            >
              Create account
            </CKButton>
          </div>
        </div>
      </CKBox>
    </>
  );
}
